/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Checkbox, ICheckboxStyleProps, ICheckboxStyles } from '@fluentui/react/lib/Checkbox';
import { FocusZone, FocusZoneDirection } from '@fluentui/react/lib/FocusZone';
import { IListProps, List } from '@fluentui/react/lib/List';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { CompositeEntityStore } from 'src/luis/types/stores/entities/CompositeEntityStore';

export type CompositeEntitiesScreenProps = {
    /**
     * The composite entities to list.
     */
    compositeEntities: CompositeEntityStore[];

    /**
     * The selected composite entities' ids to be migrated.
     */
    selectedIds: string[];

    /**
     * A callback notifies change in the selection of the
     * composite entities to be migrated.
     */
    onSelectionChange: (id: string, checked: boolean) => void;
};

const messages = defineMessages({
    description: {
        id: 'entityMigrationModal.compositeEntitiesScreen.description',
        defaultMessage: "You have {entitiesCount} composite entities. Select the entities you'd like to upgrade:"
    }
});

const { styleComponent } = getStylistV2('CompositeEntitiesScreen');
const ScrollableList = styleComponent<IListProps>(List)('ScrollableList', { height: '274px', overflow: 'auto' });

const checkboxStyles = classNamesFunction<ICheckboxStyleProps, ICheckboxStyles>()({ root: { margin: '8px 0' } });

/**
 * Displays the screen of the multi-selection for
 * the composite entities to be migrated.
 */
export const CompositeEntitiesScreen = (props: CompositeEntitiesScreenProps) => {
    const { compositeEntities, selectedIds, onSelectionChange } = props;
    const { localize } = useLocalization();

    return (
        <Stack tokens={{ childrenGap: 20 }} styles={{ root: { minHeight: '380px' } }}>
            <LuisText>{localize(messages.description, { entitiesCount: compositeEntities.length })}</LuisText>

            <FocusZone direction={FocusZoneDirection.vertical} isCircularNavigation>
                <ScrollableList
                    /**
                     * The spreading out here to force the List to re-render when the
                     * selectedIds prop changes.
                     */
                    items={[...compositeEntities]}
                    onRenderCell={(entity: CompositeEntityStore) => (
                        <Checkbox
                            label={entity.name}
                            styles={checkboxStyles}
                            checked={selectedIds.includes(entity.id as string)}
                            onChange={(_, checked) => onSelectionChange(entity.id as string, checked)}
                        />
                    )}
                />
            </FocusZone>
        </Stack>
    );
};
